import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import GoogleOneTapPrompt from './auth/GoogleOneTapPrompt';
import './LandingPage.css';

const getFirstName = (user) => {
  const candidate = user?.firstName
    || user?.first_name
    || user?.display_name
    || user?.displayName
    || user?.name
    || user?.email
    || user?.primaryEmail
    || '';

  const normalized = String(candidate).trim();
  if (!normalized) {
    return 'there';
  }

  return normalized.split('@')[0].split(/\s+/)[0];
};

const getDashboardPath = (user) => {
  switch (user?.role) {
    case 'admin':
    case 'super_admin':
      return '/app/event-simple';
    case 'teacher':
      return '/app/teacher/schedule';
    case 'event_simple':
      return '/app/event-simple';
    case 'simple_room_controls':
      return '/app/room-controls';
    default:
      return '/app/profile';
  }
};

// Feature ID: M010-F0001 - Navigation Header
const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, loading } = useAuth();
  const isSignedIn = Boolean(user);
  const firstName = getFirstName(user);
  const dashboardPath = getDashboardPath(user);

  return (
    <nav className="landing-nav">
      <div className="nav-container">
        <Link to="/" className="nav-logo">
          🎬 StudioAEye
        </Link>
        
        <button 
          className="mobile-menu-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? '✕' : '☰'}
        </button>

        <div className={`nav-links ${mobileMenuOpen ? 'open' : ''}`}>
          <a href="#features">Features</a>
          <a href="#how-it-works">How It Works</a>
          <a href="#testimonials">Testimonials</a>
          <a href="#faq">FAQ</a>
          <a href="http://dancebelfast.com" target="_blank" rel="noopener noreferrer">Schedule</a>
        </div>

        <div className={`nav-actions ${mobileMenuOpen ? 'open' : ''}`}>
          <Link to="/hire" className="btn-ghost">Venue Hire</Link>
          {loading ? (
            <span className="nav-session-loading" aria-label="Checking signed-in session" />
          ) : isSignedIn ? (
            <Link to={dashboardPath} className="nav-session-link" aria-label={`Open SEye Dashboard for ${firstName}`}>
              <span className="nav-session-greeting">Hi {firstName}</span>
              <span className="nav-session-destination">SEye Dashboard</span>
            </Link>
          ) : (
            <>
              <Link to="/register" className="btn-ghost">Create Account</Link>
              <Link to="/login" className="btn-primary">Login</Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

// Feature ID: M010-F0002 - Hero Section
const HeroSection = () => (
  <section className="hero-section">
    <div className="hero-content">
      <span className="hero-badge">🎬 Dance Studio Management</span>
      <h1>Smart Studio Management<br />Made Simple</h1>
      <p className="hero-subtitle">
        Book rooms, control climate, and manage your dance studio—all from one powerful platform.
      </p>
      <div className="hero-buttons">
        <Link to="/hire" className="btn-primary btn-large">Explore Venue Hire</Link>
        <Link to="/register" className="btn-outline btn-large">Create Account →</Link>
      </div>
      <div className="hero-trust">
        <span>✓ Guided sequence path for new users</span>
      </div>
    </div>
    <div className="hero-visual">
      <div className="hero-mockup">
        <div className="mockup-header">
          <span className="mockup-dot red"></span>
          <span className="mockup-dot yellow"></span>
          <span className="mockup-dot green"></span>
        </div>
        <div className="mockup-content">
          <div className="mockup-sidebar">
            <div className="mockup-item"></div>
            <div className="mockup-item active"></div>
            <div className="mockup-item"></div>
          </div>
          <div className="mockup-main">
            <div className="mockup-card"></div>
            <div className="mockup-card"></div>
            <div className="mockup-card small"></div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Feature ID: M010-F0003 - Features Grid
const FeaturesSection = () => {
  const features = [
    {
      icon: '📅',
      title: 'Smart Booking',
      description: 'Book rooms in seconds with our intuitive calendar interface. Real-time availability and conflict detection.'
    },
    {
      icon: '🌡️',
      title: 'Climate Control',
      description: 'Automatic temperature management for comfortable studios. Integrates with Home Assistant.'
    },
    {
      icon: '👥',
      title: 'User Management',
      description: 'Manage teachers, students, and staff with role-based access. Organisation-level controls.'
    },
    {
      icon: '📱',
      title: 'Mobile App',
      description: 'Access everything on the go with our Android & iOS apps. Book rooms from anywhere.'
    },
    {
      icon: '📊',
      title: 'Analytics',
      description: 'Track bookings, revenue, and studio utilisation. Make data-driven decisions.'
    },
    {
      icon: '🔔',
      title: 'Notifications',
      description: 'Automated reminders and real-time updates. Never miss a booking change.'
    }
  ];

  return (
    <section id="features" className="features-section">
      <div className="section-container">
        <span className="section-badge">Features</span>
        <h2>Everything You Need to Run Your Studio</h2>
        <p className="section-subtitle">
          Powerful tools designed specifically for dance studio management
        </p>
        <div className="features-grid">
          {features.map((feature, index) => (
            <div key={index} className="feature-card">
              <span className="feature-icon">{feature.icon}</span>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Feature ID: M010-F0004 - How It Works
const HowItWorksSection = () => {
  const steps = [
    {
      number: '1',
      title: 'Sign Up',
      description: 'Create your account in minutes. No credit card required.'
    },
    {
      number: '2',
      title: 'Add Rooms',
      description: 'Set up your studio rooms, schedules, and pricing.'
    },
    {
      number: '3',
      title: 'Start Booking',
      description: 'Invite teachers and start accepting bookings immediately.'
    }
  ];

  return (
    <section id="how-it-works" className="how-it-works-section">
      <div className="section-container">
        <span className="section-badge">How It Works</span>
        <h2>Get Started in 3 Easy Steps</h2>
        <div className="steps-container">
          {steps.map((step, index) => (
            <React.Fragment key={index}>
              <div className="step-card">
                <span className="step-number">{step.number}</span>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
              {index < steps.length - 1 && (
                <div className="step-connector">→</div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
};

// Feature ID: M010-F0005 - Testimonials
const TestimonialsSection = () => {
  const testimonials = [
    {
      rating: 5,
      quote: "StudioAEye transformed how we manage bookings. The climate control integration alone saves us hours every week.",
      author: "Sarah Chen",
      title: "Dance Academy Owner"
    },
    {
      rating: 5,
      quote: "Finally, a booking system that understands dance studios. The mobile app is a game-changer for our teachers.",
      author: "Michael Torres",
      title: "Studio Manager"
    },
    {
      rating: 5,
      quote: "Our students love seeing the schedule online. And the automated reminders have reduced no-shows by 40%.",
      author: "Emma Williams",
      title: "Ballet School Director"
    }
  ];

  return (
    <section id="testimonials" className="testimonials-section">
      <div className="section-container">
        <span className="section-badge">Testimonials</span>
        <h2>Loved by Studio Owners</h2>
        <div className="testimonials-grid">
          {testimonials.map((testimonial, index) => (
            <div key={index} className="testimonial-card">
              <div className="testimonial-rating">
                {'⭐'.repeat(testimonial.rating)}
              </div>
              <blockquote>"{testimonial.quote}"</blockquote>
              <div className="testimonial-author">
                <div className="author-avatar">
                  {testimonial.author.charAt(0)}
                </div>
                <div className="author-info">
                  <strong>{testimonial.author}</strong>
                  <span>{testimonial.title}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Feature ID: M010-F0006 - FAQ Accordion
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "Is there a free trial?",
      answer: "Yes! Get started with a free 14-day trial. No credit card required. Experience all features before committing."
    },
    {
      question: "What devices are supported?",
      answer: "StudioAEye works on any modern browser, plus we have native Android and iOS apps for mobile access."
    },
    {
      question: "Can I integrate with Google Calendar?",
      answer: "Yes, we offer full Google Calendar sync for all bookings. Your teachers can see studio bookings alongside their personal calendar."
    },
    {
      question: "How does climate control work?",
      answer: "We integrate with Home Assistant to automatically control HVAC based on room bookings. Set your preferred temperatures and let the system handle the rest."
    },
    {
      question: "Is my data secure?",
      answer: "Absolutely. We use industry-standard encryption and never share your data. Your studio information stays private and secure."
    }
  ];

  return (
    <section id="faq" className="faq-section">
      <div className="section-container">
        <span className="section-badge">FAQ</span>
        <h2>Frequently Asked Questions</h2>
        <div className="faq-list">
          {faqs.map((faq, index) => (
            <div 
              key={index} 
              className={`faq-item ${openIndex === index ? 'open' : ''}`}
            >
              <button 
                className="faq-question"
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                aria-expanded={openIndex === index}
              >
                <span>{faq.question}</span>
                <span className="faq-icon">{openIndex === index ? '−' : '+'}</span>
              </button>
              <div className="faq-answer">
                <p>{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Feature ID: M010-F0007 - CTA Banner
const CTABanner = () => (
  <section className="cta-section">
    <div className="section-container">
      <h2>Ready to Transform Your Studio?</h2>
      <p>Contact us to get started with StudioAEye</p>
      <div className="cta-buttons">
        <Link to="/login" className="btn-white btn-large">Login</Link>
        <a href="mailto:info@studioaeye.com" className="btn-outline-white btn-large">Contact Us</a>
      </div>
    </div>
  </section>
);

// Feature ID: M010-F0008 - Footer
// Feature ID: M010-F0010 - App Download Links
const Footer = () => (
  <footer className="landing-footer">
    <div className="footer-container">
      <div className="footer-grid">
        <div className="footer-brand">
          <span className="footer-logo">🎬 StudioAEye</span>
          <p>Smart studio management made simple.</p>
          <div className="app-badges">
            <a href="/downloads/" className="app-badge" title="Download Android App">
              <span>📱</span> Android App
            </a>
          </div>
        </div>
        
        <div className="footer-links">
          <h4>Product</h4>
          <a href="#features">Features</a>
          <a href="http://dancebelfast.com" target="_blank" rel="noopener noreferrer">Schedule</a>
          <a href="/downloads/">Mobile App</a>
        </div>
        
        <div className="footer-links">
          <h4>Company</h4>
          <a href="#about">About</a>
          <a href="#contact">Contact</a>
        </div>
        
        <div className="footer-links">
          <h4>Support</h4>
          <a href="#faq">FAQ</a>
          <a href="mailto:support@studioaeye.com">Help</a>
        </div>
      </div>
      
      <div className="footer-bottom">
        <p>© 2026 StudioAEye. All rights reserved.</p>
        <div className="footer-legal">
          <a href="#privacy">Privacy Policy</a>
          <a href="#terms">Terms of Service</a>
        </div>
      </div>
    </div>
  </footer>
);

// Main LandingPage Component
const LandingPage = () => {
  const { isAuthenticated, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleGoogleOneTapCredential = async (response) => {
    try {
      const userData = await loginWithGoogle({
        idToken: response.credential,
        selectBy: response.select_by || response.selectBy,
      });
      if (!userData?.pendingApproval) {
        navigate(getDashboardPath(userData), { replace: true });
      }
    } catch (err) {
      console.debug('Google One Tap landing sign-in unavailable:', err.message || err);
    }
  };

  return (
    <div className="landing-page">
      <GoogleOneTapPrompt
        disabled={isAuthenticated}
        onCredential={handleGoogleOneTapCredential}
      />
      <Navigation />
      <main>
        <HeroSection />
        <FeaturesSection />
        <HowItWorksSection />
        <TestimonialsSection />
        <FAQSection />
        <CTABanner />
      </main>
      <Footer />
    </div>
  );
};

export default LandingPage;
